import { ReactNode, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { BaseAnalyticsFilter } from '@repo/shared/dtos/analytics/AnalyticsFilterDto';
import AnalyticsFilters from '../AnalyticsFilters';
import AnalyticsExportButton from '../export/AnalyticsExportButton';
import { AnalyticsCategory } from '../config';
import { categoryAccent, cssVars } from './theme';
import { urlSync } from '../filters/utils/urlSync';
import { filterStorage } from '../filters/utils/filterStorage';
import { useIsMobile } from '../../../hooks/useIsMobile';

interface AnalyticsPageLayoutProps {
  category: AnalyticsCategory;
  children: ReactNode;
  showBackButton?: boolean;
  customTitle?: string;
  filters?: BaseAnalyticsFilter;
  onFiltersChange?: (filters: BaseAnalyticsFilter) => void;
  showFilters?: boolean;
  showExport?: boolean;
}

export default function AnalyticsPageLayout({
  category,
  children,
  showBackButton = true,
  customTitle,
  filters = {
    experienceIds: [],
    unitIds: [],
  },
  onFiltersChange = () => {},
  showFilters = true,
  showExport = true,
}: AnalyticsPageLayoutProps) {
  const navigate = useNavigate();

  const handleBackClick = useCallback(() => {
    if (filters.granularity === 'day') {
      const cleanedFilters: BaseAnalyticsFilter = {
        ...filters,
        granularity: 'month',
        startDate: undefined,
        endDate: undefined,
      };

      try {
        urlSync.saveFiltersToUrl(cleanedFilters);
      } catch (error) {
        console.warn('Failed to save filters to URL:', error);
      }

      if (filterStorage.isAvailable()) {
        try {
          filterStorage.save(cleanedFilters);
        } catch (error) {
          console.warn('Failed to save filters to localStorage:', error);
        }
      }
    }
    navigate({ to: '/team-insights' });
  }, [filters, navigate]);

  const isMobile = useIsMobile();
  const IconComponent = category.icon;
  const title = customTitle || category.title;
  const accent = categoryAccent[category.id] || categoryAccent['session-performance'];

  return (
    <div
      className="flex min-h-full flex-col"
      style={{ ...cssVars, ['--anl-accent' as any]: accent }}
    >
      <div className="flex-shrink-0 border-b border-[var(--anl-border)] bg-[var(--anl-surface)] shadow-lg">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex min-w-0 flex-1 items-center space-x-3">
              {showBackButton && (
                <button
                  onClick={handleBackClick}
                  className="flex flex-shrink-0 items-center space-x-2 rounded-md p-1 text-[var(--anl-text-muted)] transition-colors hover:bg-[var(--anl-surface-elev)] hover:text-[var(--anl-text)]"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
              )}

              <div className="flex min-w-0 flex-1 items-center space-x-3">
                <div className="flex-shrink-0 rounded-lg p-2" style={{ backgroundColor: accent }}>
                  <span className="inline-flex h-5 w-5 text-white">
                    <IconComponent className="h-5 w-5" />
                  </span>
                </div>
                <h1
                  className={`truncate font-semibold text-[var(--anl-text)] ${isMobile ? 'text-base' : 'text-xl'}`}
                >
                  {title}
                </h1>
              </div>
            </div>
          </div>
        </div>

        {showFilters && (
          <AnalyticsFilters
            filters={filters}
            onFiltersChange={onFiltersChange}
            category={category}
            actions={showExport ? <AnalyticsExportButton filters={filters} /> : undefined}
          />
        )}
      </div>

      <div className={`flex-1 ${isMobile ? 'p-3' : 'p-6'}`}>{children}</div>
    </div>
  );
}
